import { AlertTriangle, ArrowDown, ArrowUp } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface ScraperFilters {
  page?: number;
  limit?: number;
  status?: any;
  category?: string;
  remote_utility_id?: number;
  search_data?: string;
  name?: string;
  location?: string;
  type?: string;
  date_from?: string;
  date_to?: string;
  priority?: number[];
}

export interface FilterConfig {
  // Filter parameter names in URL
  paramNames: {
    status: string;
    category: string;
    search: string;
    page: string;
    name?: string;
    location?: string;
    type?: string;
  };

  // Default values
  defaults: {
    page: number;
    limit: number;
    status: number[];
    category: string;
    search: string;
    name?: string;
  };

  // Custom parsers for specific filter types
  parsers?: {
    status?: (value: string) => number[];
    category?: (value: string) => string;
    search?: (value: string) => string;
    name?: (value: string) => string;
  };

  // Custom serializers for specific filter types
  serializers?: {
    status?: (value: number[]) => string;
    category?: (value: string) => string;
    search?: (value: string) => string;
    name?: (value: string) => string;
  };
}

export interface UseUrlFiltersReturn {
  filters: ScraperFilters;
  page: number;
  setPage: (page: number) => void;
  handleApplyFilters: (newFilters: Partial<ScraperFilters>) => void;
  handleResetFilters: () => void;
  handlePageChange: (newPage: number) => void;
  handleSearch: (searchTerm: string, paramName?: string) => void;
  getFiltersFromUrl: () => ScraperFilters;
}

/**
 * Configurable hook for managing URL-based filters
 * Can be used across different components with different filter configurations
 */
export const useUrlFilters = (
  config: FilterConfig,
  remoteUtilityId: number,
): UseUrlFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(config.defaults.page);

  // Memoize the config to prevent unnecessary re-renders
  const memoizedConfig = useMemo(
    () => config,
    [
      config.paramNames.status,
      config.paramNames.category,
      config.paramNames.search,
      config.paramNames.page,
      config.defaults.page,
      config.defaults.limit,
      config.parsers?.status,
      config.parsers?.category,
      config.parsers?.search,
      config.serializers?.status,
      config.serializers?.category,
      config.serializers?.search,
      config.serializers?.name,
      config.paramNames.name,
    ],
  );

  // Parse filters from URL params
  const getFiltersFromUrl = useCallback((): ScraperFilters => {
    const urlFilters: ScraperFilters = {
      page,
      limit: memoizedConfig.defaults.limit,
      remote_utility_id: remoteUtilityId,
    };

    // Parse status array
    const statusParam = searchParams.get(memoizedConfig.paramNames.status);
    if (statusParam && statusParam !== "ALL_STATUS") {
      if (memoizedConfig.parsers?.status) {
        urlFilters.status = memoizedConfig.parsers.status(statusParam);
      } else {
        urlFilters.status = statusParam.split(",").map(Number);
      }
    }

    // Parse category
    const categoryParam = searchParams.get(memoizedConfig.paramNames.category);
    if (categoryParam && categoryParam !== "ALL_CATEGORIES") {
      if (memoizedConfig.parsers?.category) {
        urlFilters.category = memoizedConfig.parsers.category(categoryParam);
      } else {
        urlFilters.category = categoryParam;
      }
    }

    // Parse search query
    const searchParam = searchParams.get(memoizedConfig.paramNames.search);
    if (searchParam) {
      if (memoizedConfig.parsers?.search) {
        urlFilters.search_data = memoizedConfig.parsers.search(searchParam);
      } else {
        urlFilters.search_data = searchParam;
      }
    }

    const nameParam = searchParams.get(memoizedConfig.paramNames.name);
    if (nameParam && nameParam !== "ALL_CATEGORIES") {
      if (memoizedConfig.parsers?.name) {
        urlFilters.name = memoizedConfig.parsers.name(nameParam);
      } else {
        urlFilters.name = nameParam;
      }
    }

    return urlFilters;
  }, [searchParams, page, remoteUtilityId, memoizedConfig]);

  const [filters, setFilters] = useState<ScraperFilters>(() => {
    // Initialize with default values to avoid calling getFiltersFromUrl during initialization
    return {
      page: memoizedConfig.defaults.page,
      limit: memoizedConfig.defaults.limit,
      remote_utility_id: remoteUtilityId,
    };
  });

  // Update filters when URL changes
  useEffect(() => {
    setFilters(getFiltersFromUrl());
  }, [getFiltersFromUrl]);

  // Update page when URL page param changes
  useEffect(() => {
    const pageParam = searchParams.get(memoizedConfig.paramNames.page);
    if (pageParam) {
      setPage(parseInt(pageParam, 10));
    }
  }, [searchParams, memoizedConfig.paramNames.page]);

  const handleApplyFilters = useCallback(
    (newFilters: Partial<ScraperFilters>) => {
      const params = new URLSearchParams(searchParams);

      // Clear existing filter params
      params.delete(memoizedConfig.paramNames.status);
      params.delete(memoizedConfig.paramNames.category);
      params.delete(memoizedConfig.paramNames.page);
      params.delete(memoizedConfig.paramNames.name);

      // Add new filter params
      if (newFilters.status && newFilters.status.length > 0) {
        const statusValue = memoizedConfig.serializers?.status
          ? memoizedConfig.serializers.status(newFilters.status)
          : newFilters.status.join(",");
        params.set(memoizedConfig.paramNames.status, statusValue);
      }

      if (newFilters.category) {
        const categoryValue = memoizedConfig.serializers?.category
          ? memoizedConfig.serializers.category(newFilters.category)
          : newFilters.category;
        params.set(memoizedConfig.paramNames.category, categoryValue);
      }

      if (newFilters.name) {
        const nameValue = memoizedConfig.serializers?.name
          ? memoizedConfig.serializers.name(newFilters.name)
          : newFilters.name;
        params.set(memoizedConfig.paramNames.name, nameValue);
      }

      // Reset to page 1 when applying filters
      params.set(memoizedConfig.paramNames.page, "1");
      setPage(1);

      setSearchParams(params);
      console.log("debug filters", newFilters);
    },
    [searchParams, setSearchParams, memoizedConfig, setPage],
  );

  const handleResetFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete(memoizedConfig.paramNames.status);
    params.delete(memoizedConfig.paramNames.category);
    params.delete(memoizedConfig.paramNames.page);
    params.delete(memoizedConfig.paramNames.search);
    params.delete(memoizedConfig.paramNames.name);
    setPage(1);
    setSearchParams(params);
  }, [searchParams, setSearchParams, memoizedConfig, setPage]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set(memoizedConfig.paramNames.page, newPage.toString());
      setSearchParams(params);
      setPage(newPage);
    },
    [searchParams, setSearchParams, memoizedConfig.paramNames.page, setPage],
  );

  const handleSearch = useCallback(
    (
      searchTerm: string,
      paramName: string = memoizedConfig.paramNames.search,
    ) => {
      const params = new URLSearchParams(searchParams);
      console.log(memoizedConfig);

      if (searchTerm.trim()) {
        const searchValue = memoizedConfig.serializers?.search
          ? memoizedConfig.serializers.search(searchTerm)
          : searchTerm;

        params.set(memoizedConfig.paramNames.search, searchValue);
      } else {
        params.delete(memoizedConfig.paramNames.search);
      }

      // Reset to page 1 when searching
      params.set(memoizedConfig.paramNames.page, "1");
      setPage(1);

      setSearchParams(params);
    },
    [searchParams, setSearchParams, memoizedConfig, setPage],
  );

  return {
    filters,
    page,
    setPage,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
    getFiltersFromUrl,
  };
};

/**
 * Default configuration for service request filters
 */
export const defaultServiceFilterConfig: FilterConfig = {
  paramNames: {
    status: "status",
    category: "category",
    search: "query",
    page: "page",
    name: "name",
  },
  defaults: {
    page: 1,
    limit: 50,
    status: [],
    category: "ALL_CATEGORIES",
    search: "",
    name: "ALL_CATEGORIES",
  },
};

// /**
//  * Hook for service request filters with default configuration
//  */
// export const useServiceRequestFilters = (remoteUtilityId: number) => {
//   return useUrlFilters(serviceRequestFilterConfig, remoteUtilityId);
// };

/**
 * Hook for generic filters with custom configuration
 */
export const useCustomFilters = (
  config: FilterConfig,
  remoteUtilityId: number,
) => {
  return useUrlFilters(config, remoteUtilityId);
};

/**
 * Utility function to create a memoized filter configuration
 * This prevents infinite re-renders when the config object is passed to useCustomFilters
 */
export const createFilterConfig = (
  paramNames: FilterConfig["paramNames"],
  defaults: FilterConfig["defaults"],
  parsers?: FilterConfig["parsers"],
  serializers?: FilterConfig["serializers"],
): FilterConfig => {
  return {
    paramNames,
    defaults,
    parsers,
    serializers,
  };
};

export const getAddressStringForConsumer = (
  addressMap: any,
  isDirect: boolean = false,
): string => {
  if (isDirect) {
    return `${addressMap?.premise || ""}, ${addressMap?.area || ""}, ${
      addressMap?.state || ""
    }`;
  }
  return `${addressMap?.hierarchyNames?.premise || ""}, ${
    addressMap?.hierarchyNames?.area || ""
  }, ${addressMap?.hierarchyNames?.state || ""}`;
};

export const renderPriority = (value: string): React.ReactElement => {
  if (value === "High") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} />
        High
      </span>
    );
  }
  if (value === "Emergency") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} />
        Emergency
      </span>
    );
  }
  if (value === "Critical") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} />
        Critical
      </span>
    );
  }
  if (value === "Medium") {
    return (
      <span
        style={{
          color: "#d97706",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowUp size={16} style={{ color: "#d97706" }} />
        Medium
      </span>
    );
  }
  if (value === "Normal") {
    return (
      <span
        style={{
          color: "#d97706",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowUp size={16} style={{ color: "#d97706" }} />
        Normal
      </span>
    );
  }
  if (value === "Low") {
    return (
      <span
        style={{
          color: "#2563eb",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowDown size={16} style={{ color: "#2563eb" }} />
        Low
      </span>
    );
  }
  if (value === "NA") {
    return <span>{value}</span>;
  }
  return <span>{value}</span>;
};
